// Planning area: calculations (components/calculations/*), Season board, Schedule board.
export const planning = {
  filters: {
    allDestinations: "All destinations",
    allGarants: "All garants",
    allLifecycles: "All lifecycles",
    allGuides: "All guides",
  },
  calc: {
    empty: "No calculation yet — build the cost plan, margin and sell price here.",
    newCalculation: "New calculation",
    new: "New",
    defaultTitle: "Calculation",
    activate: "Activate",
    activateHint: "Make this the source of truth for apply/REAL (demotes siblings)",
    applied: "applied {{date}}",
    deleteTitle: "Delete calculation",
    deleteMessage:
      "Delete “{{title}}”? Items are deleted with it. Applied prices on the trip/order stay as they are.",
    dayTransportItem: "Day {{day}} — transport",
  },
  params: {
    title: "Parameters",
    paxCount: "Calculation pax",
    commissionPct: "Commission %",
    markupPct: "Markup %",
    modeMarkup: "markup",
    modeCommission: "commission",
    marginModeHint: "Same arithmetic — just the label (guided markup vs FIT-DMC commission)",
    sellCurrency: "Sell currency",
    intlFlightPerPax: "Intl flight / pax",
    passThrough: "(pass-through)",
    roundingStep: "Rounding step",
    sensitivityPax: "Sensitivity pax (comma-separated, blank = auto)",
    fxRates: "FX rates (1 unit = N CZK)",
    refreshFromSettings: "Refresh from settings",
    removeRate: "Remove rate",
  },
  items: {
    title: "Cost items ({{count}})",
    byDay: "by day",
    byDayHint: "Their excel day-grid — display order only, the math runs on sums",
    prefillFromItinerary: "Prefill from itinerary",
    importFromComponents: "Import from components",
    addItem: "Add item",
    empty:
      "No cost items — add them by hand, from the component catalog, or prefill from the itinerary.",
    colCategory: "Category",
    colCost: "Cost",
    colCur: "Cur",
    colScope: "Scope",
    colQty: "Qty",
    colOcc: "Occ",
    colOccHint: "per room only",
    colDay: "Day",
    itemNamePlaceholder: "Item name",
    catalogChip: "⛓ catalog",
    catalogChipHint: "Linked to catalog",
    addFromCatalog: "Add from component catalog…",
    perGroup: "per group",
    perPax: "per pax",
  },
  sellLines: {
    title: "Sell lines",
    subtitle: "how the sell price splits on the contract/offer — line 1 absorbs the remainder",
    addLine: "Add line",
    defaultLineLabel: "Trip price",
    newLineLabel: "New line",
    empty:
      "Single line — the whole sell price as “Trip price”. Add lines to split (e.g. activity package, NZETA).",
    defaultHint: "default — gets the remainder",
    margin: "margin",
    perPaxValue: "{{value}} / pax",
    unnamed: "(unnamed)",
    saveFirstHint: "save first to assign newly added items",
  },
  results: {
    title: "Results",
    livePreview: "live preview — unsaved",
    nettoPerPax: "Netto / pax ({{count}} pax)",
    programPerPax: "Program / pax (+{{pct}} % {{mode}})",
    intlFlightPerPax: "Intl flight / pax",
    sellPricePerPax: "SELL PRICE / pax",
    profitPerPax: "Profit / pax · total @ {{count}}",
    singleSupplement: "Single room suppl.",
    computedValue: "computed {{value}} {{currency}}",
    overrideHint: "Manual override — blank uses the computed per-room figure",
    noTripLength: "no trip length — set duration to quote",
    perPaxValue: "{{value}} / pax",
    insuranceMeta_one: "({{count}} day, table {{version}})",
    insuranceMeta_few: "({{count}} days, table {{version}})",
    insuranceMeta_other: "({{count}} days, table {{version}})",
    outsideTable: "outside the table — quote manually",
    sensitivityTitle: "Occupancy sensitivity · break-even {{breakEven}}",
    bePax: "{{count}} pax",
    beNever: "never",
    colPax: "Pax",
    colProfitTotal: "Profit total",
    colProfitPerPax: "/ pax",
  },
  actions: {
    title: "Actions",
    saveFirst: "Save first — actions use the saved calculation.",
    applyToTrip: "Apply to trip…",
    pushToComponents: "Push to components",
    pushHint:
      "Adds catalog-linked items missing from the trip's Components tab. Never edits or removes existing lines.",
    pushAdded: "Added {{count}}",
    pushAlready: "already there {{count}}",
    pushNoLink: "no catalog link {{count}}",
    generateOrderItems: "Generate order items…",
    applyTitle: "Apply {{amount}} to trip",
    updatePriceFrom: "Update trip “price from”",
    updateSingleSupplement: "Update single-room supplement",
    noDepartures: "No departures on this trip.",
    currentPrice: "(now {{price}})",
    genPreviewTitle: "Order items after generate",
    genReplaced_one: "Replaces {{count}} previously generated line",
    genReplaced_few: "Replaces {{count}} previously generated lines",
    genReplaced_other: "Replaces {{count}} previously generated lines",
    genKept_one: "keeps {{count}} manual line",
    genKept_few: "keeps {{count}} manual lines",
    genKept_other: "keeps {{count}} manual lines",
    writeItems: "Write items",
  },
  real: {
    title: "REAL — plan vs reality",
    pickDeparture: "Pick a departure…",
    load: "Load",
    colCategory: "Category",
    colPlan: "Plan",
    colBooked: "Booked",
    colActual: "Actual",
    totalWithCurrency: "Total ({{currency}})",
    revenue: "Revenue",
    cost: "cost",
    bookings_one: "{{count}} booking",
    bookings_few: "{{count}} bookings",
    bookings_other: "{{count}} bookings",
  },
  season: {
    subtitle: "The whole selling quarter at a glance — codes, garant, fill & go/no-go.",
    empty: "No departures in this window",
    deadlineDeposit: "Deposit",
    deadlineFoc: "FOC",
    deadlineTicket: "Ticket",
    overdue: "{{count}}d overdue",
    today: "today",
    inDays: "{{count}}d",
    atRiskHint: "{{amount}} {{currency}} at risk on unsold seats",
    inheritedGarant: "Inherited from destination: {{name}}",
    noGarant: "— none —",
    waitlistShort: "+{{count}} wl",
    openVelin: "Open velín",
  },
  schedule: {
    subtitle:
      "Guide assignments across the season — drag to reassign, the engine flags overlaps & tight connections.",
    conflicts_one: "{{count}} conflict",
    conflicts_few: "{{count}} conflicts",
    conflicts_other: "{{count}} conflicts",
    quarter: "Quarter",
    sixWeeks: "6 weeks",
    jumpToNow: "Jump back to now",
    today: "Today",
    onlyConflicts: "Only conflicts",
    emptyGuides: "No active guides to schedule",
    guideColumn: "Guide",
    fallbackPool: "Other destinations — fallback pool ({{count}})",
    unassignedTitle: "Unassigned departures",
    unassignedHint: "drag onto a guide to assign (tentative)",
    allAssigned: "Every bookable departure has a confirmed lead 🎉",
    homeDestination: "Home destination: {{name}}",
    assignmentFallback: "Assignment",
    departureFallback: "Departure",
    tentativeSuffix: "(tentative)",
    role: "Role",
    makeTentative: "Make tentative",
    findReplacement: "Find replacement",
    whoCanCover: "Who can cover this?",
    noOtherGuides: "No other active guides.",
    removeFromDeparture: "Remove from departure",
    openDepartureVelin: "Open departure velín →",
  },
};
